import React from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, FileText, Edit2, MapPin } from 'lucide-react';

const ViewEventModal = ({
  isOpen,
  onClose,
  event,
  onEdit
}) => {
  if (!isOpen || !event) return null;

  const eventTitle = event.title || event.activity || event.eventTitle || 'Event Details';
  const eventTime = event.time || event.eventTime || '';
  const eventDesc = event.description || event.summary || event.eventDesc || '';
  const dayNum = event.dayNumber || (event.dayIdx !== undefined ? event.dayIdx + 1 : '');

  const pickupName = event.pickup_drop?.pickup?.name || event.pickup_drop?.pickup_location?.name || (typeof event.pickup_drop?.pickup === 'string' ? event.pickup_drop.pickup : '') || '';
  const dropName = event.pickup_drop?.drop?.name || event.pickup_drop?.drop_location?.name || (typeof event.pickup_drop?.drop === 'string' ? event.pickup_drop.drop : '') || '';

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 md:p-6 bg-slate-900/50 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)] my-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white shrink-0">
          <div className="min-w-0 pr-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">
                {dayNum ? `Day ${dayNum} Event` : 'Event'}
              </span>
              {eventTime && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#067978]/10 text-[#067978] flex items-center gap-1 font-mono shrink-0">
                  <Clock size={10} /> {eventTime}
                </span>
              )}
            </div>
            <h3 className="text-base sm:text-lg font-bold text-slate-800 tracking-tight mt-0.5 break-words">
              {eventTitle}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 overflow-y-auto space-y-3.5 text-xs text-slate-700 flex-1">
          {/* Timing details if available */}
          {eventTime && (
            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block mb-1">
                Scheduled Timing
              </span>
              <div className="flex items-center gap-2 font-mono font-medium text-slate-800 text-xs">
                <Clock size={13} className="text-[#067978]" />
                <span>{eventTime}</span>
              </div>
            </div>
          )}

          {/* Description */}
          {eventDesc ? (
            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block mb-1">
                Event Details / Description
              </span>
              <div className="flex items-start gap-2 text-slate-600">
                <FileText size={13} className="text-slate-400 shrink-0 mt-0.5" />
                <p className="font-normal leading-relaxed whitespace-pre-line text-xs">{eventDesc}</p>
              </div>
            </div>
          ) : (
            <div className="p-3 bg-slate-50/50 rounded-xl border border-slate-100 text-slate-400 text-xs italic">
              No additional description provided.
            </div>
          )}

          {/* Pickup & Drop Points if any */}
          {(pickupName || dropName) && (
            <div className="p-3 bg-slate-50/80 rounded-xl border border-slate-100 space-y-2">
              <span className="text-[10px] uppercase font-semibold text-slate-400 block">
                Pickup &amp; Drop Locations
              </span>
              {pickupName && (
                <div className="flex items-center gap-2 text-xs text-slate-700">
                  <MapPin size={13} className="text-emerald-600 shrink-0" />
                  <span>Pickup: {pickupName}</span>
                </div>
              )}
              {dropName && (
                <div className="flex items-center gap-2 text-xs text-slate-700 pt-1 border-t border-slate-200/60">
                  <MapPin size={13} className="text-rose-600 shrink-0" />
                  <span>Drop: {dropName}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200/70 rounded-lg transition-all cursor-pointer"
          >
            Close
          </button>
          {onEdit && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onEdit(event);
              }}
              className="px-3.5 py-1.5 bg-[#067978] hover:bg-[#056564] text-white text-xs font-medium rounded-lg shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Edit2 size={12} />
              <span>Edit Event</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ViewEventModal;
